import { useFocusEffect } from '@react-navigation/native';
import { format } from 'date-fns';
import React, { useCallback, useEffect, useState } from 'react';
import { View } from 'react-native';
import { SnagClient } from '../../api';

import tw from '../../config/tailwind';
import { useAuthContext } from '../../hooks/useAuthContext';
import { PaginatedResult } from '../../shared/types/paginatedResult';
import { ProjectResponse } from '../../shared/types/projects';
import { SnagResponse, SnagStatus } from '../../shared/types/snags';
import { UserRole } from '../../shared/types/users';
import FullWidthButton from '../shared/FullWidthButton';
import GenericTable from '../shared/GenericTable';
import ViewAction from '../shared/ViewAction';
import EyeSVG from '../svg/EyeSVG';
import AddSnagModal from './modals/AddSnagModal';

interface SnagTableProps {
  project: ProjectResponse;
}
const SnagTable: React.FC<SnagTableProps> = ({ project }) => {
  const [page, setPage] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const [showNewSnagModal, setShowNewSnagModal] = useState(false);

  const { user, activeCompany } = useAuthContext();

  const [paginatedResult, setPaginatedResult] =
    useState<PaginatedResult<SnagResponse>>();

  useEffect(() => {
    if (user?.role === UserRole.Admin || activeCompany)
      SnagClient.getAllByProjectId(project.id, page).then((result) => {
        setPaginatedResult(result);
      });
    return () => {
      setPaginatedResult(undefined);
    };
  }, [page, activeCompany?.id, refresh]);

  useFocusEffect(
    useCallback(() => {
      setRefresh((old) => old + 1);
    }, [])
  );

  return (
    <View style={tw`bg-white p-8 mb-6`}>
      <GenericTable
        paginatedResult={paginatedResult}
        headers={{
          name: {
            title: 'Issue name',
            width: '300px',
            bold: true,
          },
          status: {
            title: 'Completion',
            width: '132px',
            format: (status) =>
              status === SnagStatus.Complete
                ? 'Complete'
                : status === SnagStatus.InProgress
                ? 'In progress'
                : 'Open',
          },
          completedAt: {
            title: 'Date complete',
            width: '132px',
            format: (date) => (date ? format(date, 'dd/MM/yyyy') : '-'),
          },
        }}
        actions={(snag) => [
          <ViewAction
            to={{
              screen: 'authenticatedStackNavigator',
              params: {
                screen: 'projectStack',
                params: {
                  screen: 'snag',
                  params: {
                    projectId: project.id,
                    snagId: snag.id,
                  },
                },
              },
            }}
          />,
        ]}
        setPage={setPage}
      />

      <View style={tw`flex flex-row justify-between`}>
        <FullWidthButton
          variant='transparent'
          buttonStyle={tw`py-2 pr-4 w-48`}
          underline
          onPress={() => setShowNewSnagModal(true)}
        >
          + New issue
        </FullWidthButton>
      </View>

      <AddSnagModal
        project={project}
        visible={showNewSnagModal}
        setVisible={setShowNewSnagModal}
        onSnagAdded={() => setRefresh((x) => x + 1)}
      />
    </View>
  );
};

export default SnagTable;
